import { useState, useMemo, useCallback } from 'react';

/**
 * Reusable pagination hook supporting both client-side and server-side modes.
 *
 * Client mode (backward compatible):
 *   usePagination(itemsArray, itemsPerPage)
 *
 * Server mode:
 *   usePagination({ totalItems, initialPage, initialPageSize })
 *
 * @param {Array|Object} itemsOrConfig - items array (client) or config object (server)
 * @param {number} [itemsPerPage=10] - page size for client mode
 */
export const usePagination = (itemsOrConfig, itemsPerPage = 10) => {
    const isClientMode = Array.isArray(itemsOrConfig);

    // Server config defaults
    const serverConfig = !isClientMode ? itemsOrConfig || {} : {};
    const {
        totalItems: serverTotalItems,
        initialPage = 1,
        initialPageSize = 10,
        totalPages: serverTotalPages,
    } = serverConfig;

    const [currentPage, setCurrentPage] = useState(initialPage);
    const [pageSize, setPageSize] = useState(
        isClientMode ? itemsPerPage : initialPageSize,
    );

    // Client mode derived values
    const clientTotalPages = isClientMode ? Math.ceil(itemsOrConfig.length / pageSize) : 0;

    const totalPages = isClientMode
        ? clientTotalPages
        : serverTotalPages ?? Math.ceil((serverTotalItems || 0) / pageSize);

    const totalItems = isClientMode ? itemsOrConfig.length : serverTotalItems ?? 0;

    const paginatedItems = useMemo(() => {
        if (!isClientMode) return [];
        const startIndex = (currentPage - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        return itemsOrConfig.slice(startIndex, endIndex);
    }, [itemsOrConfig, currentPage, pageSize, isClientMode]);

    const goToPage = useCallback(
        (page) => {
            const validPage = Math.max(1, Math.min(page, totalPages || 1));
            setCurrentPage(validPage);
        },
        [totalPages],
    );

    const nextPage = useCallback(() => {
        goToPage(currentPage + 1);
    }, [currentPage, goToPage]);

    const prevPage = useCallback(() => {
        goToPage(currentPage - 1);
    }, [currentPage, goToPage]);

    const reset = useCallback(() => {
        setCurrentPage(1);
    }, []);

    const handlePageSizeChange = useCallback(
        (newSize) => {
            setPageSize(newSize);
            setCurrentPage(1);
        },
        [],
    );

    // Server pagination helpers
    const offset = (currentPage - 1) * pageSize;
    const limit = pageSize;
    const paginationParams = useMemo(
        () => ({ page: currentPage, limit: pageSize, offset }),
        [currentPage, pageSize, offset],
    );

    return {
        currentPage,
        page: currentPage,
        pageSize,
        limit,
        offset,
        totalPages,
        totalItems,
        paginatedItems,
        goToPage,
        setPage: goToPage,
        nextPage,
        prevPage,
        reset,
        setPageSize: handlePageSizeChange,
        hasNextPage: currentPage < totalPages,
        hasPrevPage: currentPage > 1,
        paginationParams,
    };
};

/**
 * Server-side pagination hook.
 * Thin wrapper around usePagination for semantic clarity.
 *
 * @param {{ totalItems?: number, totalPages?: number, initialPage?: number, initialPageSize?: number }} config
 */
export const useServerPagination = (config = {}) => usePagination(config);

export default usePagination;
